import {useRouter} from 'next/router';
import {HTMLAttributes, SyntheticEvent, useCallback, useState} from 'react';
import {StoreErrorChecker} from '../../common/checkers/StoreErrorChecker';
import {
  SCHEME_CLOSE_WEBVIEW,
  SESSION_STORAGE_SPA_INITIAL_PATH,
} from '../../common/constant';
import {t} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import {useBodyOverscrollLock} from '../../common/shared/hooks/useBodyOverscrollLock';
import {useGetTopMenuHidden} from '../../common/shared/hooks/useGetTopMenuHidden';
import useResizeObserver from '../../common/shared/hooks/useResizeObserver';
import {twMerge} from '../../common/twMerge';
import useTypedSelector from '../../common/useTypedSelector';
import {cancelLoginAgreement} from '../../domain/user/agreement/util/cancelLoginAgreement';
import {ApiErrorBoundary} from '../../errors/ApiErrorBoundary.class';
import {
  CommonMobileHeaderType,
  ExceptionMobileHeaderType,
} from '../../header/constants';
import {LOG_CLICK_EVENT} from '../../logging/logEvent';
import {putTiaraObjectProps} from '../../logging/putTiaraObjectProps';
import {Sticky} from '../atoms/Sticky/Sticky';
import {StickyLocation} from '../atoms/Sticky/StickyLocation.enum';
import {Navibar} from '../organisms/Navibar/Navibar';
import {MOBILE_LAYOUT_GNB_ID, MOBILE_LAYOUT_ID} from './constant';
import {FooterType} from './LayoutFooter/FooterType.enum';
import {LayoutFooter} from './LayoutFooter/LayoutFooter';
import {MobileLayoutCommentHeader} from './MobileLayoutCommentHeader';
import {MobileLayoutCommonHeader} from './MobileLayoutCommonHeader';
import {MobileLayoutConfig} from './MobileLayoutConfig.interface';
import {MobileLayoutContentHomeHeader} from './MobileLayoutContentHomeHeader';
import {MobileLayoutGlobalAuthHeader} from './MobileLayoutGlobalAuthHeader';
import {MobileLayoutGnb} from './MobileLayoutGnb';
import {MobileLayoutNewWindowHeader} from './MobileLayoutNewWindowHeader';
import {MobileLayoutRegisterHeader} from './MobileLayoutRegisterHeader';
import {MobileLayoutSearchHeader} from './MobileLayoutSearchHeader';
import {MobileLayoutSetOverviewHeader} from './MobileLayoutSetOverviewHeader';
import {MobileLayoutViewerHeader} from './MobileLayoutViewerHeader';

interface Props extends MobileLayoutConfig, HTMLAttributes<HTMLDivElement> {}

export function MobileLayout({
  children,
  shouldShowHeader,
  shouldShowFooter: _shouldShowFooter,
  shouldShowFooterLinkMenu,
  shouldShowGnb,
  headerType,
  headerTitle: _headerTitle,
  seriesId,
  closeWindowText,
  headerCustomTiaraObject,
  footerType = FooterType.Common,
  className,
  isLayoutV2,
  ...props
}: Props) {
  const {isWebView, isKakaotalk} = requestContext.requestInfo.display;
  const router = useRouter();

  const {shouldPageHideTopMenu, isTopMenuHidden} = useGetTopMenuHidden();
  useBodyOverscrollLock();

  const {customHeaderTitle, customShouldShowFooter, hideBackButton} =
    useTypedSelector(state => ({
      customHeaderTitle: state.header.customTitle,
      customShouldShowFooter: state.footer.mobile?.isExposed,
      hideBackButton: state.header.hideBackButton,
    }));
  const _headerCustomTiaraObject =
    useTypedSelector(state => state.header.customTiaraObject) ||
    headerCustomTiaraObject;

  const headerTitle = customHeaderTitle || _headerTitle;
  const shouldShowFooter = customShouldShowFooter ?? _shouldShowFooter;

  const shouldCheckRequiredAgreement = useTypedSelector(
    state => !!state.header.shouldCheckRequiredAgreement
  );

  const historyBack = useCallback(() => {
    // 현재 경로가 SPA의 '초기 경로'와 같은지 확인
    // Next.js 라우터는 history.length를 직접 노출하지 않으므로,
    // sessionStorage에 저장된 initialPath와 현재 router.asPath를 비교합니다.
    const initialPath = sessionStorage.getItem(
      SESSION_STORAGE_SPA_INITIAL_PATH
    );

    if (router.asPath === initialPath) {
      isWebView
        ? window.location.assign(SCHEME_CLOSE_WEBVIEW)
        : router.push('/');
      sessionStorage.removeItem(SESSION_STORAGE_SPA_INITIAL_PATH);
    } else {
      router.back();
    }
  }, [router, isWebView]);

  const onClickBack = useCallback(
    (e: SyntheticEvent) => {
      LOG_CLICK_EVENT('뒤로가기', e, historyBack);
    },
    [historyBack]
  );

  const onClickHome = useCallback(
    (e: SyntheticEvent) => {
      function handler() {
        router.push('/');
      }
      LOG_CLICK_EVENT('메인홈_클릭', e, handler);
    },
    [router]
  );

  const [headerHeight, setHeaderHeight] = useState(0);
  const {ref: headerRef} = useResizeObserver<HTMLDivElement>({
    onResize: param => {
      setHeaderHeight(param.height ?? 0);
    },
  });

  return (
    <div
      className={twMerge(
        'flex grow flex-col',
        isLayoutV2 && 'bg-theme-bg-base-alt',
        className
      )}
      {...props}
    >
      <Sticky
        id={MOBILE_LAYOUT_ID}
        location={StickyLocation.Top}
        isMobile={true}
        isWebView={isWebView}
        className={
          shouldPageHideTopMenu
            ? twMerge('transition-transform duration-300 will-change-transform')
            : undefined
        }
        style={
          shouldPageHideTopMenu
            ? {
                transform: isTopMenuHidden
                  ? `translateY(-${headerHeight}px)`
                  : 'translateY(0)',
              }
            : undefined
        }
      >
        <div
          ref={headerRef}
          id={MOBILE_LAYOUT_GNB_ID}
          className={twMerge(
            'top-0 z-150 flex w-full flex-col items-center',
            (headerType === ExceptionMobileHeaderType.MobileViewer ||
              headerType === ExceptionMobileHeaderType.ContentHome) &&
              'fixed'
          )}
          {...putTiaraObjectProps(
            _headerCustomTiaraObject ?? {
              click: {layer1: '상단바'},
            }
          )}
        >
          {shouldShowHeader && (
            <>
              {/* 웹뷰에 미노출할 헤더 */}
              {!isWebView && (
                <>
                  {Object.values(CommonMobileHeaderType).includes(
                    headerType as CommonMobileHeaderType
                  ) && (
                    <MobileLayoutCommonHeader
                      headerType={headerType as CommonMobileHeaderType}
                      headerTitle={headerTitle}
                      isKakaotalk={isKakaotalk}
                      onClickBack={onClickBack}
                      onClickHome={onClickHome}
                    />
                  )}
                  {headerType === ExceptionMobileHeaderType.ContentHome && (
                    <MobileLayoutContentHomeHeader
                      onClickBack={onClickBack}
                      onClickHome={onClickHome}
                    />
                  )}
                  {headerType === ExceptionMobileHeaderType.NewWindow && (
                    <MobileLayoutNewWindowHeader
                      headerType={ExceptionMobileHeaderType.NewWindow}
                      headerTitle={headerTitle}
                      closeWindowText={closeWindowText}
                      onClickBack={onClickBack}
                      historyBack={historyBack}
                    />
                  )}
                  {headerType ===
                    ExceptionMobileHeaderType.NewWindowTwoDepth && (
                    <MobileLayoutNewWindowHeader
                      headerType={ExceptionMobileHeaderType.NewWindowTwoDepth}
                      headerTitle={headerTitle}
                      closeWindowText={closeWindowText}
                      onClickBack={onClickBack}
                      historyBack={historyBack}
                    />
                  )}
                  {headerType === ExceptionMobileHeaderType.Search && (
                    <MobileLayoutSearchHeader onClickBack={onClickBack} />
                  )}
                  {headerType === ExceptionMobileHeaderType.Comment && (
                    <MobileLayoutCommentHeader
                      headerTitle={headerTitle}
                      onClickBack={onClickBack}
                    />
                  )}
                  {headerType === ExceptionMobileHeaderType.MobileViewer && (
                    <MobileLayoutViewerHeader />
                  )}
                  {headerType ===
                    ExceptionMobileHeaderType.UserLoginAgreement && (
                    <Navibar
                      title={t('signup_terms_title')}
                      onClickBack={() =>
                        cancelLoginAgreement({
                          isMobile: true,
                          shouldCheckRequiredAgreement,
                        })
                      }
                    />
                  )}
                  {headerType === ExceptionMobileHeaderType.SetOverview && (
                    <MobileLayoutSetOverviewHeader
                      seriesId={seriesId}
                      onClickBack={onClickBack}
                    />
                  )}
                </>
              )}
              {headerType === ExceptionMobileHeaderType.Register && (
                <MobileLayoutRegisterHeader headerTitle={headerTitle} />
              )}
              {/* 웹뷰에 노출할 헤더 */}
              {isWebView && (
                <>
                  {headerType === ExceptionMobileHeaderType.GlobalAuth && (
                    <MobileLayoutGlobalAuthHeader
                      headerTitle={headerTitle}
                      hideBackButton={hideBackButton}
                      onClickBack={onClickBack}
                    />
                  )}
                </>
              )}
            </>
          )}
          {shouldShowGnb && <MobileLayoutGnb />}
        </div>
      </Sticky>
      <div className="flex flex-col grow">
        <ApiErrorBoundary refreshOnRetry={true}>
          <StoreErrorChecker>{children}</StoreErrorChecker>
        </ApiErrorBoundary>
      </div>
      {shouldShowFooter && (
        <LayoutFooter
          isMobile
          shouldShowFooterLinkMenu={shouldShowFooterLinkMenu}
          footerType={footerType}
        />
      )}
    </div>
  );
}
